/**
 * Datadog RUM (Real User Monitoring) Configuration - Monutrack
 *
 * Provides initialization for Datadog browser monitoring with environment-aware settings.
 * Supports TanStack Router for SPA route tracking and React error boundary integration.
 *
 * Features:
 * - Session replay for debugging
 * - Performance monitoring
 * - Error tracking
 * - User action tracking
 * - Custom logging integration
 *
 * Configuration:
 * - Production: Full monitoring with session replay sampling
 * - Staging/Development: Monitoring enabled but with different sampling rates
 * - Test: Monitoring disabled
 */

import { datadogRum } from '@datadog/browser-rum'
import { reactPlugin } from '@datadog/browser-rum-react'

/**
 * Environment detection
 * Determines the current environment based on hostname and environment variables
 */
const getEnvironment = (): 'dev' | 'live' => {
  // Check for test environment first
  if (import.meta.env.DEV) {
    return 'dev'
  }
  return 'live'
}

/**
 * Get sampling rate based on environment
 * Adjust these values based on your monitoring budget and needs
 */
const getSamplingRates = (env: string) => {
  switch (env) {
    case 'live':
      return {
        sessionSampleRate: 100, // Monitor all sessions
        sessionReplaySampleRate: 100 // Record 100% of sessions
      }
    case 'dev':
      return {
        sessionSampleRate: 100, // Monitor all sessions in dev
        sessionReplaySampleRate: 20 // Record 20% of sessions for dev
      }
    default:
      return {
        sessionSampleRate: 0,
        sessionReplaySampleRate: 0
      }
  }
}

/**
 * Initialize Datadog RUM
 *
 * Call this as early as possible in the application lifecycle,
 * ideally before mounting the React app.
 *
 * @returns true if initialization succeeded, false otherwise
 */
export const initializeDatadog = (): boolean => {
  try {
    const env = getEnvironment()

    const { sessionSampleRate, sessionReplaySampleRate } = getSamplingRates(env)

    // Datadog credentials - can be overridden via environment variables
    const applicationId = import.meta.env.VITE_DATADOG_APPLICATION_ID || '599689d3-3e64-4f4d-af24-9fd52eee10a5'
    const clientToken = import.meta.env.VITE_DATADOG_CLIENT_TOKEN || 'pub06a7cda51e9e2aae0e2581c3382812c1'

    // Initialize Datadog RUM
    datadogRum.init({
      applicationId,
      clientToken,
      site: 'datadoghq.com',
      service: 'monutrack',
      env,

      // Version tracking - use package version
      version: '0.1.0',

      // Sampling rates based on environment
      sessionSampleRate,
      sessionReplaySampleRate,

      // Privacy settings
      defaultPrivacyLevel: 'mask-user-input',

      // Track user interactions
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,

      // React plugin for better React integration
      plugins: [reactPlugin({ router: false })],

      // Before sending data, you can modify or enrich it
      beforeSend: (event) => {
        // Add custom context here if needed
        // For example, add tenant information and full hostname
        if (event.type === 'view' || event.type === 'action') {
          // Safely extract tenant from hostname with defensive checks
          let tenant = 'unknown'
          let hostname = 'unknown'

          if (typeof window !== 'undefined' && window.location?.hostname) {
            hostname = window.location.hostname

            // If hostname contains dots, use first segment as tenant
            // Otherwise use the whole hostname (handles localhost, IPs, etc.)
            tenant = hostname.includes('.') ? hostname.split('.')[0] : hostname

            // Sanitize: remove non-alphanumeric chars and truncate to 64 chars
            tenant = tenant.replace(/[^a-zA-Z0-9-_]/g, '').substring(0, 64) || 'unknown'
          }

          event.context = {
            ...event.context,
            tenant,
            hostname
          }
        }
        return true
      }
    })

    // Start session replay recording
    datadogRum.startSessionReplayRecording()

    console.log(`[Datadog] RUM initialized for environment: ${env}`)
    return true
  } catch (error) {
    console.error('[Datadog] Failed to initialize RUM:', error)
    return false
  }
}

/**
 * Track custom user actions
 *
 * Use this to track important user interactions that aren't automatically captured
 *
 * @param name - Action name (e.g., "employee_created", "evaluation_submitted")
 * @param context - Additional context data
 */
export const trackUserAction = (name: string, context?: Record<string, unknown>) => {
  try {
    datadogRum.addAction(name, context)
  } catch (error) {
    console.error('[Datadog] Failed to track user action:', error)
  }
}

/**
 * Set user information for tracking
 *
 * Call this after user authentication to associate sessions with users
 *
 * @param user - User information
 */
export const setDatadogUser = (user: {
  id: string
  name?: string
  email?: string
  [key: string]: unknown
}) => {
  try {
    datadogRum.setUser(user)
  } catch (error) {
    console.error('[Datadog] Failed to set user:', error)
  }
}

/**
 * Clear user information
 *
 * Call this on logout to stop associating actions with the user
 */
export const clearDatadogUser = () => {
  try {
    datadogRum.clearUser()
  } catch (error) {
    console.error('[Datadog] Failed to clear user:', error)
  }
}

/**
 * Track route changes for SPA navigation
 *
 * Since we're using TanStack Router (not React Router), we need to manually track route changes.
 * This should be called in a TanStack Router plugin or navigation listener.
 *
 * @param viewName - The name/path of the view being navigated to
 */
export const trackRouteChange = (viewName: string) => {
  try {
    datadogRum.startView(viewName)
  } catch (error) {
    console.error('[Datadog] Failed to track route change:', error)
  }
}

/**
 * Add custom error tracking
 *
 * Use this to manually report errors that aren't caught by React error boundaries
 *
 * @param error - Error object or message
 * @param context - Additional context about the error
 */
export const trackError = (error: Error | string, context?: Record<string, unknown>) => {
  try {
    const errorObj = typeof error === 'string' ? new Error(error) : error
    datadogRum.addError(errorObj, context)
  } catch (err) {
    console.error('[Datadog] Failed to track error:', err)
  }
}

/**
 * Add custom timing metrics
 *
 * Use this to track performance of specific operations
 *
 * @param name - Metric name
 * @param duration - Duration in milliseconds
 * @param context - Additional context
 */
export const trackTiming = (name: string, duration: number, context?: Record<string, unknown>) => {
  try {
    datadogRum.addTiming(name)
    datadogRum.addAction(name, { ...(context ?? {}), duration })
  } catch (error) {
    console.error('[Datadog] Failed to track timing:', error)
  }
}

/**
 * Set global context that will be added to all RUM events
 *
 * @param key - Context key
 * @param value - Context value
 */
export const setGlobalContext = (key: string, value: unknown) => {
  try {
    datadogRum.setGlobalContextProperty(key, value)
  } catch (error) {
    console.error('[Datadog] Failed to set global context:', error)
  }
}

/**
 * Remove global context property
 *
 * @param key - Context key to remove
 */
export const removeGlobalContext = (key: string) => {
  try {
    datadogRum.removeGlobalContextProperty(key)
  } catch (error) {
    console.error('[Datadog] Failed to remove global context:', error)
  }
}

export default {
  initialize: initializeDatadog,
  trackUserAction,
  setUser: setDatadogUser,
  clearUser: clearDatadogUser,
  trackRouteChange,
  trackError,
  trackTiming,
  setGlobalContext,
  removeGlobalContext
}
